import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialsApi, type CredentialDetail } from '@/api/credentials';
import { credentialSchemasApi, type CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import { errorMessage } from '@/api/client';
import { decryptCredential, type CredentialPayload } from '@/crypto/sodium';
import Modal from '@/components/Modal';
import StatPill from '@/components/ui/StatPill';
import { useAuthStore } from '@/stores/auth';
import { getRevealTimeout } from '@/stores/preferences';
import { copySecret } from '@/lib/clipboard';
import { formatDateTime, formatRelative } from '@/lib/format';
import i18n from '@/i18n';
import { EyeIcon, Row, metaValueToString, inferFallbackSecretAttrs } from '@/features/vault/detail/shared';
import { VersionsSection } from '@/features/vault/detail/VersionsSection';
import { SharesSection } from '@/features/vault/detail/SharesSection';
import { EditForm } from '@/features/vault/detail/EditForm';
import { FileBlobDownload } from '@/features/vault/detail/FileBlobDownload';
import { CredentialTagsSection } from '@/features/vault/detail/CredentialTagsSection';
import { BreakGlassToggleSection } from '@/features/vault/detail/BreakGlassToggleSection';

interface Props {
  id: string;
  onClose: () => void;
  onDeleted?: () => void;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
}

export default function VaultDetailModal({ id, onClose, onDeleted, keyPair }: Props) {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);

  const query = useQuery({
    queryKey: ['credential', id],
    queryFn: () => credentialsApi.get(id),
  });

  const schemaQuery = useQuery({
    queryKey: ['credential-schemas', tenantId, query.data?.type],
    queryFn: () => credentialSchemasApi.get(tenantId!, query.data!.type),
    enabled: !!tenantId && !!query.data,
    staleTime: 5 * 60 * 1000,
  });

  const title = query.data ? (
    <div>
      <h2 className="display-title-sm truncate">{query.data.title}</h2>
      <div className="flex gap-2 mt-1 text-xs">
        <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)]">v{query.data.current_version}</span>
      </div>
    </div>
  ) : (
    <h2 className="display-title-sm">{t('vault_ui.detail.credential')}</h2>
  );

  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-2xl">
      {query.isLoading && <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>}
      {query.isError && (
        <p className="text-sm text-red-600">
          {t('vault_ui.detail.error_with_message', {
            message: query.error ? String(query.error) : t('vault_ui.detail.unknown_error'),
          })}
        </p>
      )}
      {query.data && (
        <Body
          cred={query.data}
          schema={schemaQuery.data}
          keyPair={keyPair}
          onDeleted={() => {
            onClose();
            onDeleted?.();
          }}
        />
      )}
    </Modal>
  );
}

function Body({
  cred,
  schema,
  keyPair,
  onDeleted,
}: {
  cred: CredentialDetail;
  schema: CredentialTypeSchemaResponse | undefined;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const verifyPassword = useAuthStore((s) => s.verifyPassword);
  const queryClient = useQueryClient();

  const [plaintext, setPlaintext] = useState<CredentialPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmMode, setConfirmMode] = useState(false);
  const [pwdInput, setPwdInput] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});
  const [editMode, setEditMode] = useState(false);
  const revealTimersRef = useRef<Record<string, number>>({});

  // Auto-decript la deschidere: payload-ul e decriptat imediat în memorie,
  // dar fiecare câmp cu tip `secret` rămâne mascat până user-ul apasă ochiul.
  // Depinde de blob, nu doar de id: după edit, restore sau rotație `cred.id`
  // rămâne același, dar ciphertext-ul se schimbă și trebuie re-decriptat.
  useEffect(() => {
    if (editMode) return;
    let cancelled = false;
    (async () => {
      try {
        const decoded = await decryptCredential(
          {
            ciphertext: cred.ciphertext,
            nonce: cred.nonce,
            dek_wrapped: cred.dek_wrapped,
          },
          keyPair,
        );
        if (!cancelled) setPlaintext(decoded);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : i18n.t('vault_ui.detail.decrypt_error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cred.id, cred.ciphertext, cred.nonce, cred.dek_wrapped, keyPair, editMode]);

  // Curăță toate timer-ele de auto-hide la unmount.
  useEffect(() => {
    return () => {
      for (const id of Object.values(revealTimersRef.current)) {
        window.clearTimeout(id);
      }
      revealTimersRef.current = {};
    };
  }, []);

  const toggleRevealKey = (key: string) => {
    setRevealedKeys((prev) => {
      const willShow = !prev[key];
      const existing = revealTimersRef.current[key];
      if (existing) {
        window.clearTimeout(existing);
        delete revealTimersRef.current[key];
      }
      if (willShow) {
        const timeout = getRevealTimeout() * 1000;
        revealTimersRef.current[key] = window.setTimeout(() => {
          setRevealedKeys((s) => ({ ...s, [key]: false }));
          delete revealTimersRef.current[key];
        }, timeout);
      }
      return { ...prev, [key]: willShow };
    });
  };

  const deleteMutation = useMutation({
    mutationFn: () => credentialsApi.delete(cred.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      onDeleted();
    },
    onError: (err) => {
      setError(errorMessage(err, t('vault_ui.detail.delete_error')));
    },
  });

  const submitDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (!pwdInput) {
      setPwdError(t('vault_ui.detail.enter_master_password'));
      return;
    }
    setBusy(true);
    try {
      const ok = await verifyPassword(pwdInput);
      if (!ok) {
        setPwdError(t('vault_ui.detail.wrong_password'));
        setPwdInput('');
        return;
      }
      deleteMutation.mutate();
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : t('vault_ui.detail.verify_error'));
    } finally {
      setBusy(false);
    }
  };

  // Golește clipboard-ul după 30s (vezi lib/clipboard.ts).
  const copyValue = (value: string) => copySecret(value);

  const metaAttrs = (schema?.attributes ?? []).filter((a) => !a.secret);
  const secretAttrs = (schema?.attributes ?? []).filter((a) => a.secret);

  // Fallback generic când n-avem schema încă.
  const hasSchema = schema !== undefined;

  const enterEditMode = async () => {
    setError(null);
    if (plaintext) {
      setEditMode(true);
      return;
    }
    setBusy(true);
    try {
      const decoded = await decryptCredential(
        {
          ciphertext: cred.ciphertext,
          nonce: cred.nonce,
          dek_wrapped: cred.dek_wrapped,
        },
        keyPair,
      );
      setPlaintext(decoded);
      setEditMode(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault_ui.detail.decrypt_error'));
    } finally {
      setBusy(false);
    }
  };

  // IMPORTANT: toți hook-urile trebuie să fie declarate înainte de orice
  // return condiționat, ca să respecte invarianta React de hooks-order.
  // Early-return-ul pentru edit mode vine după toate useQuery-urile.
  const sharedCountQuery = useQuery({
    queryKey: ['credential-shares-count', cred.id],
    queryFn: () => credentialsApi.listShares(cred.id),
  });
  const versionsCountQuery = useQuery({
    queryKey: ['credential-versions-count', cred.id],
    queryFn: () => credentialsApi.listVersions(cred.id),
  });
  const sharedCount = (sharedCountQuery.data?.length ?? 1);
  const versionsCount = versionsCountQuery.data?.length ?? cred.current_version;

  if (editMode && schema) {
    return (
      <EditForm
        cred={cred}
        schema={schema}
        initialPlaintext={plaintext ?? {}}
        onCancel={() => setEditMode(false)}
        onSaved={() => {
          setEditMode(false);
          setPlaintext(null);
          queryClient.invalidateQueries({ queryKey: ['credential', cred.id] });
          queryClient.invalidateQueries({ queryKey: ['credential-versions', cred.id] });
          queryClient.invalidateQueries({ queryKey: ['credential-versions-count', cred.id] });
          queryClient.invalidateQueries({
            queryKey: ['credentials'],
            predicate: (q) => q.queryKey[0] === 'credentials',
          });
        }}
      />
    );
  }

  const daysSinceUpdate = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(cred.updated_at).getTime()) / (1000 * 60 * 60 * 24),
    ),
  );
  const updatedLabel =
    daysSinceUpdate === 0
      ? t('vault_ui.detail.updated_today')
      : daysSinceUpdate < 30
        ? t('vault_ui.detail.updated_days', { count: daysSinceUpdate })
        : daysSinceUpdate < 365
          ? t('vault_ui.detail.updated_months', { count: Math.floor(daysSinceUpdate / 30) })
          : t('vault_ui.detail.updated_years', { count: Math.floor(daysSinceUpdate / 365) });

  return (
    <div className="space-y-4">
      {/* Stat pills — metrici credențial */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label={t('vault_ui.detail.stat_versions')} value={versionsCount} color="purple" />
        <StatPill
          label={t('vault_ui.detail.stat_shared_with')}
          value={sharedCount}
          color="cyan"
          delta={
            sharedCount > 1
              ? { value: t('vault_ui.detail.shares_count', { count: sharedCount - 1 }), direction: 'up' }
              : { value: t('vault_ui.detail.only_you'), direction: 'neutral' }
          }
        />
        <StatPill
          label={t('vault_ui.detail.stat_accesses')}
          value={cred.access_count}
          color="rose"
          delta={
            cred.last_accessed_at
              ? {
                  value: t('vault_ui.detail.last_access', {
                    when: formatRelative(cred.last_accessed_at),
                  }),
                  direction: 'neutral',
                }
              : { value: t('vault_ui.detail.never'), direction: 'neutral' }
          }
        />
        <StatPill label={t('vault.col_updated')} value={updatedLabel} color="amber" />
      </div>

      <CredentialTagsSection cred={cred} />

      <BreakGlassToggleSection cred={cred} />

      <section>
        <h3 className="display-title-xs mb-2">{t('vault_ui.detail.meta_title')}</h3>
        <dl className="text-sm space-y-2">
          {hasSchema ? (
            metaAttrs.length > 0 ? (
              metaAttrs.map((attr) => (
                <Row
                  key={attr.key}
                  label={attr.label}
                  value={metaValueToString(cred.meta?.[attr.key])}
                  mono={attr.type !== 'text' && attr.type !== 'textarea'}
                />
              ))
            ) : (
              <p className="text-xs text-[var(--text-muted)]">{t('vault_ui.detail.no_meta_attrs')}</p>
            )
          ) : (
            <>
              <Row label="URL" value={cred.url} mono />
              <Row label={t('vault.col_username')} value={cred.username} mono />
            </>
          )}
          <Row
            label={t('vault_ui.detail.expires')}
            value={cred.expires_at ? formatDateTime(cred.expires_at) : null}
          />
          <Row label={t('vault_ui.detail.created')} value={formatDateTime(cred.created_at)} />
          <Row label={t('vault.col_updated')} value={formatDateTime(cred.updated_at)} />
        </dl>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="display-title-xs inline-flex items-center gap-2">
            {t('vault_ui.detail.encrypted_content')}
            {!plaintext && !error && (
              <span
                className="spinner-inline"
                aria-label={t('vault_ui.detail.decrypting_label')}
                title={t('vault_ui.detail.decrypting_title')}
              />
            )}
          </h3>
          <div className="flex gap-2">
            <button
              className="btn-outline text-xs"
              onClick={enterEditMode}
              disabled={busy || !schema}
              title={!schema ? t('vault_ui.detail.waiting_schema') : t('common.edit')}
            >
              {t('common.edit')}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
            {error}
          </div>
        )}

        {plaintext ? (
          <dl className="space-y-3 fade-in">
            {/* Ordinea e dictată de `schema.attributes` (setabilă în Settings → Credential attributes). */}
            {(secretAttrs.length > 0 ? secretAttrs : inferFallbackSecretAttrs(plaintext)).map(
              (attr) => {
                const raw = plaintext[attr.key];
                if (raw === undefined || raw === null || raw === '') return null;
                const value = String(raw);
                const isMasked = !revealedKeys[attr.key] && attr.type === 'secret';
                return (
                  <div key={attr.key}>
                    <div className="flex items-center justify-between mb-1">
                      <dt className="text-[var(--text-muted)] text-xs uppercase">{attr.label}</dt>
                      <div className="flex gap-2 items-center">
                        {attr.type === 'secret' && (
                          <button
                            type="button"
                            aria-label={isMasked ? t('vault_ui.detail.show') : t('vault.hide')}
                            title={
                              isMasked
                                ? t('vault_ui.detail.show_auto_hide', { seconds: getRevealTimeout() })
                                : t('vault.hide')
                            }
                            className="inline-flex items-center justify-center text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300 p-0.5"
                            onClick={() => toggleRevealKey(attr.key)}
                          >
                            <EyeIcon open={!isMasked} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300"
                          onClick={() => copyValue(value)}
                        >
                          {t('vault_ui.detail.copy')}
                        </button>
                      </div>
                    </div>
                    {attr.type === 'file' ? (
                      <FileBlobDownload
                        base64={value}
                        filename={
                          typeof cred.meta?.['filename'] === 'string'
                            ? (cred.meta['filename'] as string)
                            : 'secure-file'
                        }
                      />
                    ) : attr.type === 'textarea' ? (
                      <dd className="whitespace-pre-wrap bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded p-3 text-sm font-mono text-[var(--text)]">
                        {value}
                      </dd>
                    ) : (
                      <dd className="font-mono text-sm break-all bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-3 py-2 text-[var(--text)]">
                        {isMasked ? '••••••••' : value}
                      </dd>
                    )}
                  </div>
                );
              },
            )}
          </dl>
        ) : error ? (
          <p className="text-xs text-[var(--text-muted)]">{t('vault_ui.detail.decrypt_failed')}</p>
        ) : (
          // Skeleton placeholders cu shimmer — dă feedback vizual continuu
          // despre progresul de decriptare. Numărul de row-uri = numărul de
          // secretAttrs din schema (sau fallback 3 dacă n-avem schema încă).
          <dl className="space-y-3" aria-busy="true" aria-label={t('vault_ui.detail.decrypting_label')}>
            {Array.from({ length: secretAttrs.length || 3 }).map((_, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div
                    className="skeleton"
                    style={{
                      width: 90,
                      height: 10,
                      border: 'none',
                      background: 'var(--bg-surface)',
                    }}
                  />
                  <div
                    className="skeleton"
                    style={{
                      width: 40,
                      height: 10,
                      border: 'none',
                      background: 'var(--bg-surface)',
                    }}
                  />
                </div>
                <div className="skeleton" style={{ height: 34 }} />
              </div>
            ))}
          </dl>
        )}
      </section>

      <VersionsSection cred={cred} keyPair={keyPair} schema={schema} />

      <SharesSection
        cred={cred}
        keyPair={keyPair}
        tenantId={useAuthStore.getState().currentTenantId ?? ''}
      />

      <section className="pt-4 border-t border-[var(--border)]">
        {!confirmMode ? (
          <button
            type="button"
            className="text-xs text-red-600 dark:text-red-400 hover:underline"
            onClick={() => {
              setConfirmMode(true);
              setPwdError(null);
              setPwdInput('');
            }}
          >
            {t('vault_ui.detail.delete_credential')}
          </button>
        ) : (
          <form onSubmit={submitDelete} className="space-y-3">
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              {t('vault_ui.detail.delete_confirm_title', { title: cred.title })}
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              {t('vault_ui.detail.delete_hint')}
            </p>
            <input
              type="password"
              className="input font-mono"
              placeholder={t('auth.master_password')}
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              autoFocus
              disabled={busy || deleteMutation.isPending}
            />
            {pwdError && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
                {pwdError}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                className="btn bg-red-600 text-white hover:bg-red-700 text-sm"
                disabled={busy || deleteMutation.isPending}
              >
                {busy
                  ? t('vault_ui.detail.verifying_password')
                  : deleteMutation.isPending
                    ? t('vault_ui.detail.deleting')
                    : t('vault_ui.detail.delete_permanently')}
              </button>
              <button
                type="button"
                className="btn-outline text-sm"
                onClick={() => {
                  setConfirmMode(false);
                  setPwdInput('');
                  setPwdError(null);
                }}
                disabled={busy || deleteMutation.isPending}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
